import React, { createContext, useContext, useCallback } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';

const ConnectionApiContext = createContext(null);

export function ConnectionApiProvider({ children }) {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ORGANIZATION);

  const getConnection = useCallback(
    async (skip, limit, connection_type) => {
      if (!skip) {
        skip = 0;
      }
      if (!limit) {
        limit = 10;
      }
      try {
        let url = `/v1/connections?skip=${skip}&limit=${limit}`;

        if (connection_type) {
          url += `&connection_type=${connection_type}`;
        }

        const response = await get(url);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection:', error);
        throw error;
      }
    },
    [get],
  );

  const getConnectionById = useCallback(
    async (connectionId) => {
      try {
        const response = await get(`/v1/connections/${connectionId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const createConnection = useCallback(
    async (connection) => {
      try {
        const response = await post(`/v1/connections`, connection);
        return response.data;
      } catch (error) {
        console.error('Error creating connection:', error);
        throw error;
      }
    },
    [post],
  );

  const updateConnectionById = useCallback(
    async (connectionId, connection) => {
      try {
        const response = await put(`/v1/connections/${connectionId}`, connection);
        return response.data;
      } catch (error) {
        console.error('Error updating connection:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteConnection = useCallback(
    async (connectionId) => {
      try {
        const response = await del(`/v1/connections/${connectionId}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting connection:', error);
        throw error;
      }
    },
    [del],
  );

  const getConnectionSecretsById = useCallback(
    async (connectionId) => {
      try {
        const response = await get(`/v1/connections/${connectionId}/secrets`);
        return response.data;
      } catch (error) {
        console.error('Error fetching connection secrets by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const googleDriveConnection = useCallback(
    async (code, redirectUri, connectionId) => {
      try {
        const response = await post(`/v1/connections/google/token`, {
          code,
          redirect_uri: redirectUri,
          connection_key: connectionId,
        });
        return response.data;
      } catch (error) {
        console.error('Error exchanging Google code:', error);
        throw error;
      }
    },
    [post],
  );

  const getGoogleAccountConstants = useCallback(
    async (connectionId, connectionType) => {
      try {
        const response = await get(
          `/v1/connections/google/constants?connection_key=${connectionId}&connection_type=${connectionType}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching Google Drive constants:', error);
        throw error;
      }
    },
    [get],
  );

  const disconnectGoogleAccount = useCallback(
    async (connectionId) => {
      try {
        const response = await post(`/v1/connections/google/disconnect`, {
          connection_key: connectionId,
        });
        return response.data;
      } catch (error) {
        console.error('Error disconnect Google code:', error);
        throw error;
      }
    },
    [post],
  );

  return (
    <ConnectionApiContext.Provider
      value={{
        getConnection,
        getConnectionById,
        createConnection,
        updateConnectionById,
        deleteConnection,
        getConnectionSecretsById,
        googleDriveConnection,
        getGoogleAccountConstants,
        disconnectGoogleAccount,
      }}
    >
      {children}
    </ConnectionApiContext.Provider>
  );
}

export function useConnectionApi() {
  const context = useContext(ConnectionApiContext);
  if (!context) {
    throw new Error('useConnectionApi must be used within a ConnectionApiProvider');
  }
  return context;
}
